/**
 * Mailing page — live monitor + start/stop control + settings access.
 *
 * Layout (top-to-bottom):
 *   1. Hero status card (running + key metrics)
 *   2. Start/stop button (with preflight error toast)
 *   3. Settings + Templates quick row
 *   4. Live event feed (SSE) — auto-scroll, color-coded by kind
 *   5. Recent send log (paginated, scrollable)
 *
 * Route: /app/mailing
 */

import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Clock,
  Heart,
  Inbox,
  Loader2,
  MessageSquare,
  Moon,
  Pause,
  Play,
  PlugZap,
  Power,
  RotateCcw,
  Send,
  Settings as SettingsIcon,
  Timer,
  UserPlus,
  Users,
  WifiOff,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Card } from "@/design";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import {
  useMailingSettings,
  useMailingStatus,
  useMailingStream,
  useSendLog,
  useStartMailing,
  useStopMailing,
  useUpdateMailingSettings,
  type LiveEvent,
  type LiveEventKind,
  type MailingMode,
  type SendLogItem,
} from "@/api/mailing";
import { useStatsTimeseries } from "@/api/stats";
import { MailingSettingsSheet } from "@/components/mailing/MailingSettingsSheet";
import { ProfilesPanel } from "@/components/mailing/ProfileEditor";

export function MailingPage() {
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data: status, isLoading: statusLoading } = useMailingStatus();
  const { data: settings } = useMailingSettings();
  const updateSettings = useUpdateMailingSettings();
  const startMut = useStartMailing();
  const stopMut = useStopMailing();

  const mailingMode: MailingMode = settings?.mailing_mode ?? "classic";

  const handleModeChange = async (mode: MailingMode) => {
    if (mode === mailingMode) return;
    haptic.select();
    try {
      await updateSettings.mutateAsync({ mailing_mode: mode });
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось переключить режим");
    }
  };
  const { events, connected } = useMailingStream({ enabled: true, bufferSize: 200 });
  const { data: log } = useSendLog({ limit: 30 });
  // Hourly series for the last 24h — drives the hero chart
  const { data: ts24 } = useStatsTimeseries({ days: 1, bucket: "hour" });

  const running = !!status?.running;
  const sent24 = status?.sent_24h ?? 0;
  const failed24 = status?.failed_24h ?? 0;
  const attempts24 = sent24 + failed24;
  const successRate24 = attempts24 > 0 ? Math.round((sent24 / attempts24) * 100) : 0;
  const hourlyPoints = (ts24?.points ?? []).map((p) => p.sent);
  // Pad to 24 points so even an empty engine produces a readable axis
  const hourly = hourlyPoints.length >= 24
    ? hourlyPoints.slice(-24)
    : [...Array(24 - hourlyPoints.length).fill(0), ...hourlyPoints];

  const handleToggle = async () => {
    if (running) {
      haptic.impact("medium");
      try {
        await stopMut.mutateAsync();
        haptic.notify("success");
        toast.success("Рассылка остановлена");
      } catch (e) {
        haptic.notify("error");
        toast.error((e as Error)?.message || "Не удалось остановить");
      }
    } else {
      haptic.impact("medium");
      try {
        await startMut.mutateAsync();
        haptic.notify("success");
        toast.success("Рассылка запущена");
      } catch (e) {
        haptic.notify("error");
        if (e instanceof ApiError) {
          toast.error(e.message);
        } else {
          toast.error((e as Error)?.message || "Не удалось запустить");
        }
      }
    }
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate("/app");
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Рассылка</div>
        <button
          onClick={() => {
            haptic.select();
            setSettingsOpen(true);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Настройки"
        >
          <SettingsIcon className="size-4" />
        </button>
      </div>

      {/* Mode toggle: classic (shared pool + one template) vs profiles
          (per-account template + chat subset). */}
      <div className="flex p-1 rounded-2xl bg-white/[0.04] border border-white/[0.06]">
        {(["classic", "profiles"] as MailingMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => handleModeChange(mode)}
            className={cn(
              "flex-1 h-9 rounded-xl text-[13px] font-medium transition-colors",
              mailingMode === mode
                ? "bg-gradient-accent text-white"
                : "text-text-muted hover:text-white",
            )}
          >
            {mode === "classic" ? "Классический" : "Профили"}
          </button>
        ))}
      </div>

      {/* Hero status card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card padded={false} className="relative overflow-hidden">
          <div
            className={cn(
              "absolute inset-0 pointer-events-none transition-opacity duration-500",
              running
                ? "bg-gradient-to-br from-success/25 via-transparent to-transparent opacity-100"
                : "bg-gradient-to-br from-[#FF6B35]/20 via-transparent to-transparent opacity-100",
            )}
            aria-hidden
          />
          <div className="relative p-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium">
                  <span
                    className={cn(
                      "size-1.5 rounded-full",
                      running ? "bg-success animate-pulse" : "bg-text-dim",
                    )}
                  />
                  <span className={running ? "text-success" : "text-text-muted"}>
                    {statusLoading
                      ? "Загрузка…"
                      : running
                        ? "Рассылка идёт"
                        : "Рассылка остановлена"}
                  </span>
                </div>
                <div className="text-[26px] leading-tight font-semibold tracking-tight mt-2">
                  {running ? (
                    <>
                      Автопостинг{" "}
                      <span className="text-gradient-accent">активен</span>
                    </>
                  ) : (
                    <>
                      Запустить{"\n"}
                      <span className="text-gradient-accent">рассылку</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_auto] items-end gap-3">
              <HeroChart data={hourly} running={running} />
              <div className="text-right pb-1">
                <div className="text-[28px] leading-none font-semibold tabular-nums tracking-tight">
                  {sent24.toLocaleString("ru")}
                </div>
                <div className="text-[10px] text-text-dim uppercase tracking-[0.15em] font-medium mt-1">
                  отпр. 24ч
                </div>
                {failed24 > 0 && (
                  <div className="text-[10px] text-danger tabular-nums mt-1">
                    −{failed24} ош.
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={handleToggle}
              disabled={startMut.isPending || stopMut.isPending}
              className={cn(
                "w-full h-12 rounded-2xl font-semibold flex items-center justify-center gap-2",
                "shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)] active:scale-[0.98] transition-transform",
                "disabled:opacity-60",
                running
                  ? "bg-danger/20 text-danger border border-danger/40 shadow-none"
                  : "bg-gradient-accent text-white",
              )}
            >
              {startMut.isPending || stopMut.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : running ? (
                <Pause className="size-4 fill-danger" />
              ) : (
                <Play className="size-4 fill-white" />
              )}
              {running ? "Остановить" : "Запустить"}
            </button>

            {/* Ring + inline counters under the button */}
            <div className="flex items-center gap-3 pt-1">
              <SuccessRing percent={successRate24} />
              <div className="flex-1 grid grid-cols-2 gap-3 min-w-0">
                <InlineStat
                  icon={<MessageSquare className="size-3" />}
                  label="чатов"
                  value={status?.enabled_chats ?? 0}
                />
                <InlineStat
                  icon={<Users className="size-3" />}
                  label="акк-тов"
                  value={status?.enabled_accounts ?? 0}
                />
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Profiles panel — only in profiles mode */}
      {mailingMode === "profiles" && <ProfilesPanel />}

      {/* Live feed */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium flex items-center gap-1.5">
            <Activity className="size-3" />
            Лента событий
          </div>
          <div
            className={cn(
              "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-md",
              connected
                ? "bg-success/15 text-success"
                : "bg-white/[0.04] text-text-dim",
            )}
          >
            {connected ? (
              <PlugZap className="size-2.5" />
            ) : (
              <WifiOff className="size-2.5" />
            )}
            {connected ? "онлайн" : "офлайн"}
          </div>
        </div>

        <div
          className={cn(
            "rounded-2xl border border-white/[0.06] bg-white/[0.02]",
            "max-h-[320px] overflow-y-auto",
          )}
        >
          {events.length === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-text-muted">
              {running
                ? "Жду первое событие…"
                : "Запустите рассылку, чтобы увидеть события в реальном времени"}
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04]">
              <AnimatePresence initial={false}>
                {events.map((ev, idx) => (
                  <motion.div
                    key={`${ev.ts}-${idx}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    <EventRow ev={ev} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Send log */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium flex items-center gap-1.5">
            <Clock className="size-3" />
            Последние публикации
          </div>
          <button
            onClick={() => haptic.select()}
            className="text-xs text-text-dim hover:text-text-muted flex items-center gap-0.5 transition-colors"
          >
            Все
            <ChevronRight className="size-3" />
          </button>
        </div>

        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] max-h-[400px] overflow-y-auto">
          {(log?.items?.length ?? 0) === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-text-muted">
              Пока ничего не отправлено
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04]">
              {log!.items.map((it) => (
                <LogRow key={it.id} item={it} />
              ))}
            </div>
          )}
        </div>
      </div>

      <MailingSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function HeroChart({ data, running }: { data: number[]; running: boolean }) {
  const w = 320;
  const h = 76;
  const pad = 4;
  const max = Math.max(1, ...data);
  const min = 0;
  const range = max - min || 1;
  const n = data.length;
  const step = n > 1 ? (w - pad * 2) / (n - 1) : 0;
  const pts = data.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return { x, y, v };
  });

  const d =
    pts.length === 0
      ? ""
      : pts
          .map((p, i) => {
            if (i === 0) return `M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
            const prev = pts[i - 1];
            const cx = (prev.x + p.x) / 2;
            return `C ${cx.toFixed(1)} ${prev.y.toFixed(1)}, ${cx.toFixed(1)} ${p.y.toFixed(1)}, ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
          })
          .join(" ");
  const areaD = pts.length
    ? `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${h} L ${pts[0].x.toFixed(1)} ${h} Z`
    : "";

  const gid = React.useId();
  const peakIdx = data.reduce((best, v, i, a) => (v > a[best] ? i : best), 0);
  const peak = pts[peakIdx];
  const showPeak = max > 0;
  const isFlat = max === 0;

  // Colour: success-green when engine is running, accent-orange when idle
  const accent = running ? "#22c55e" : "#FF6B35";

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height: h }}
      aria-hidden
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.5" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Faint hourly gridline at midpoint */}
      <line
        x1={0}
        x2={w}
        y1={h / 2}
        y2={h / 2}
        stroke="rgba(255,255,255,0.04)"
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      {!isFlat && <path d={areaD} fill={`url(#${gid})`} />}
      <path
        d={d}
        fill="none"
        stroke={accent}
        strokeWidth={2}
        strokeOpacity={isFlat ? 0.35 : 1}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={isFlat ? "3 3" : undefined}
        vectorEffect="non-scaling-stroke"
        style={{ filter: isFlat ? undefined : `drop-shadow(0 0 5px ${accent}80)` }}
      />
      {showPeak && peak && (
        <g>
          <circle cx={peak.x} cy={peak.y} r={6} fill={accent} fillOpacity={0.18} />
          <circle cx={peak.x} cy={peak.y} r={3} fill={accent} />
          <circle cx={peak.x} cy={peak.y} r={1.4} fill="#fff" />
        </g>
      )}
    </svg>
  );
}

function SuccessRing({ percent }: { percent: number }) {
  const size = 52;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, percent)) / 100) * c;
  const gid = React.useId();

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="#86efac" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
          fill="none"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={`url(#${gid})`}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-[12px] font-semibold tabular-nums leading-none">
          {percent}
          <span className="text-[9px] text-text-muted">%</span>
        </div>
      </div>
    </div>
  );
}

function InlineStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] text-text-dim uppercase tracking-wider font-medium">
        <span className="size-3.5 grid place-items-center text-text-muted">{icon}</span>
        {label}
      </div>
      <div className="text-[16px] font-semibold tabular-nums leading-tight mt-0.5">
        {value}
      </div>
    </div>
  );
}

function EventRow({ ev }: { ev: LiveEvent }) {
  const meta = KIND_META[ev.kind];
  const d = ev.data;
  const time = formatTime(ev.ts);

  let title: string = meta?.label ?? ev.kind;
  let subtitle: string | null = null;

  switch (ev.kind) {
    case "hello":
      title = "Соединение установлено";
      break;
    case "engine_started":
      title = "Движок запущен";
      break;
    case "engine_stopped":
      title = "Движок остановлен";
      break;
    case "iteration_start":
      title = "Новый круг рассылки";
      subtitle = d.chats_count != null ? `${d.chats_count} чатов в очереди` : null;
      break;
    case "iteration_done":
      title = "Круг рассылки завершён";
      subtitle = d.next_in_seconds != null ? `Следующая через ${d.next_in_seconds}s` : null;
      break;
    case "iteration_empty":
      title = "Нет чатов для рассылки";
      subtitle = "Подключите хотя бы один активный чат";
      break;
    case "iteration_error":
      title = "Ошибка итерации";
      subtitle = d.error ?? null;
      break;
    case "send":
      title = d.chat_title ?? `Чат #${d.chat_id ?? "?"}`;
      subtitle = d.text_only
        ? "Отправлено без медиа (чат запрещает вложения)"
        : "Сообщение отправлено";
      break;
    case "skip":
      title = d.chat_title ?? `Чат #${d.chat_id ?? "?"}`;
      subtitle = d.reason ? `Пропуск: ${d.reason}` : "Пропуск";
      break;
    case "fail":
      title = d.chat_title ?? `Чат #${d.chat_id ?? "?"}`;
      subtitle = d.error ?? "Ошибка отправки";
      break;
    case "flood":
      title = "Flood wait";
      subtitle = d.flood_seconds != null ? `${d.flood_seconds}s ожидания` : null;
      break;
    case "quiet_mode":
      title = "Тихий режим";
      subtitle = d.seconds != null ? `Пауза ${Math.round(d.seconds / 60)} мин` : null;
      break;
    case "reaction_worker_started":
      title = "Воркер реакций запущен";
      break;
    case "reaction_worker_stopped":
      title = "Воркер реакций остановлен";
      break;
    case "reaction_iteration_done":
      title = "Цикл реакций завершён";
      subtitle = d.next_in_seconds != null ? `Следующая через ${d.next_in_seconds}s` : null;
      break;
    case "reaction_iteration_empty":
      title = "Реакции: нет действия";
      subtitle = d.reason ? `Причина: ${d.reason}` : null;
      break;
    case "reaction_iteration_error":
      title = "Ошибка воркера реакций";
      subtitle = d.error ?? null;
      break;
    case "reaction_sent":
      title = d.chat_title ?? `Чат #${d.chat_id ?? "?"}`;
      subtitle = `Реакция ${d.reaction ?? ""} поставлена`.trim();
      break;
    case "reaction_skip":
      title = d.chat_title ?? "Реакция пропущена";
      subtitle = d.reason ? `Пропуск: ${d.reason}` : "Пропуск";
      break;
    case "reaction_fail":
      title = d.chat_title ?? "Ошибка реакции";
      subtitle = d.error ?? "Не удалось поставить реакцию";
      break;
    case "reaction_flood":
      title = "Flood wait на реакции";
      subtitle = d.flood_seconds != null ? `${d.flood_seconds}s ожидания` : null;
      break;
    case "reaction_quiet_mode":
      title = "Реакции: тихий режим";
      subtitle = d.seconds != null ? `Пауза ${Math.round(d.seconds / 60)} мин` : null;
      break;
    case "join_ok":
      title = d.chat_title ?? `Чат #${d.chat_id ?? "?"}`;
      subtitle = "Аккаунт вступил в чат";
      break;
    case "join_fail":
      title = d.chat_title ?? `Чат #${d.chat_id ?? "?"}`;
      subtitle = d.error ? `Не удалось вступить: ${d.error}` : "Не удалось вступить";
      break;
    case "join_manual":
      title = d.chat_title ?? `Чат #${d.chat_id ?? "?"}`;
      subtitle = "Вступите в чат вручную из Telegram";
      break;
    case "lag":
      title = "Отставание";
      subtitle = "Слишком быстро — некоторые события пропущены";
      break;
    case "heartbeat":
      title = "пульс";
      break;
  }

  return (
    <div className="px-4 py-2.5 flex items-center gap-3">
      <div
        className={cn(
          "size-8 rounded-lg grid place-items-center shrink-0",
          meta?.bg ?? "bg-white/[0.04]",
          meta?.fg ?? "text-text-dim",
        )}
      >
        {meta?.icon ?? <Activity className="size-3.5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate">{title}</div>
        {subtitle && (
          <div className="text-[11px] text-text-muted truncate">{subtitle}</div>
        )}
      </div>
      <div className="text-[10px] text-text-dim tabular-nums shrink-0">{time}</div>
    </div>
  );
}

function LogRow({ item }: { item: SendLogItem }) {
  const isOk = item.status === "sent";
  const isFail = item.status === "failed";
  return (
    <div className="px-4 py-2.5 flex items-center gap-3">
      <div
        className={cn(
          "size-8 rounded-lg grid place-items-center shrink-0",
          isOk && "bg-success/15 text-success",
          isFail && "bg-danger/15 text-danger",
          !isOk && !isFail && "bg-white/[0.04] text-text-dim",
        )}
      >
        {isOk ? (
          <Send className="size-3.5" />
        ) : isFail ? (
          <XCircle className="size-3.5" />
        ) : (
          <Pause className="size-3.5" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate">
          {item.chat_title ?? `Чат #${item.chat_id ?? "?"}`}
        </div>
        <div className="text-[11px] text-text-muted truncate">
          {isFail
            ? item.error_text ?? item.error_code ?? "Ошибка"
            : isOk
              ? "Отправлено"
              : "Пропущено"}
        </div>
      </div>
      <div className="text-[10px] text-text-dim tabular-nums shrink-0">
        {formatTimeFromIso(item.created_at)}
      </div>
    </div>
  );
}

// ============================================================
// Event metadata
// ============================================================

const KIND_META: Partial<
  Record<LiveEventKind, { label: string; icon: React.ReactNode; bg: string; fg: string }>
> = {
  hello: {
    label: "hello",
    icon: <PlugZap className="size-3.5" />,
    bg: "bg-white/[0.04]",
    fg: "text-text-dim",
  },
  engine_started: {
    label: "engine_started",
    icon: <Power className="size-3.5" />,
    bg: "bg-success/15",
    fg: "text-success",
  },
  engine_stopped: {
    label: "engine_stopped",
    icon: <Power className="size-3.5" />,
    bg: "bg-white/[0.04]",
    fg: "text-text-dim",
  },
  iteration_start: {
    label: "mailing_round",
    icon: <RotateCcw className="size-3.5" />,
    bg: "bg-accent-soft",
    fg: "text-accent",
  },
  iteration_done: {
    label: "mailing_round_done",
    icon: <CheckCircle2 className="size-3.5" />,
    bg: "bg-accent-soft",
    fg: "text-accent",
  },
  iteration_empty: {
    label: "iteration_empty",
    icon: <Inbox className="size-3.5" />,
    bg: "bg-warning/15",
    fg: "text-warning",
  },
  iteration_error: {
    label: "iteration_error",
    icon: <AlertTriangle className="size-3.5" />,
    bg: "bg-danger/15",
    fg: "text-danger",
  },
  send: {
    label: "send",
    icon: <Send className="size-3.5" />,
    bg: "bg-success/15",
    fg: "text-success",
  },
  skip: {
    label: "skip",
    icon: <Pause className="size-3.5" />,
    bg: "bg-white/[0.04]",
    fg: "text-text-dim",
  },
  fail: {
    label: "fail",
    icon: <XCircle className="size-3.5" />,
    bg: "bg-danger/15",
    fg: "text-danger",
  },
  flood: {
    label: "flood",
    icon: <Timer className="size-3.5" />,
    bg: "bg-warning/15",
    fg: "text-warning",
  },
  quiet_mode: {
    label: "quiet_mode",
    icon: <Moon className="size-3.5" />,
    bg: "bg-white/[0.04]",
    fg: "text-text-muted",
  },
  reaction_worker_started: {
    label: "reaction_worker_started",
    icon: <Heart className="size-3.5" />,
    bg: "bg-success/15",
    fg: "text-success",
  },
  reaction_worker_stopped: {
    label: "reaction_worker_stopped",
    icon: <Heart className="size-3.5" />,
    bg: "bg-white/[0.04]",
    fg: "text-text-dim",
  },
  reaction_iteration_done: {
    label: "reaction_iteration_done",
    icon: <CheckCircle2 className="size-3.5" />,
    bg: "bg-accent-soft",
    fg: "text-accent",
  },
  reaction_iteration_empty: {
    label: "reaction_iteration_empty",
    icon: <Inbox className="size-3.5" />,
    bg: "bg-warning/15",
    fg: "text-warning",
  },
  reaction_iteration_error: {
    label: "reaction_iteration_error",
    icon: <AlertTriangle className="size-3.5" />,
    bg: "bg-danger/15",
    fg: "text-danger",
  },
  reaction_sent: {
    label: "reaction_sent",
    icon: <Heart className="size-3.5" />,
    bg: "bg-success/15",
    fg: "text-success",
  },
  reaction_skip: {
    label: "reaction_skip",
    icon: <Pause className="size-3.5" />,
    bg: "bg-white/[0.04]",
    fg: "text-text-dim",
  },
  reaction_fail: {
    label: "reaction_fail",
    icon: <XCircle className="size-3.5" />,
    bg: "bg-danger/15",
    fg: "text-danger",
  },
  reaction_flood: {
    label: "reaction_flood",
    icon: <Timer className="size-3.5" />,
    bg: "bg-warning/15",
    fg: "text-warning",
  },
  reaction_quiet_mode: {
    label: "reaction_quiet_mode",
    icon: <Moon className="size-3.5" />,
    bg: "bg-white/[0.04]",
    fg: "text-text-muted",
  },
  join_ok: {
    label: "join_ok",
    icon: <UserPlus className="size-3.5" />,
    bg: "bg-success/15",
    fg: "text-success",
  },
  join_fail: {
    label: "join_fail",
    icon: <UserPlus className="size-3.5" />,
    bg: "bg-danger/15",
    fg: "text-danger",
  },
  join_manual: {
    label: "join_manual",
    icon: <UserPlus className="size-3.5" />,
    bg: "bg-warning/15",
    fg: "text-warning",
  },
  lag: {
    label: "lag",
    icon: <Zap className="size-3.5" />,
    bg: "bg-warning/15",
    fg: "text-warning",
  },
};

// ============================================================
// Helpers
// ============================================================

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatTimeFromIso(iso: string): string {
  try {
    return formatTime(new Date(iso).getTime());
  } catch {
    return "";
  }
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}
